import React from 'react';
import { clsx } from 'clsx';

export interface CardProps {
  children: React.ReactNode;
  className?: string;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  shadow?: 'none' | 'sm' | 'md' | 'lg';
  border?: boolean;
  hover?: boolean;
  onClick?: () => void;
}

export const Card: React.FC<CardProps> = ({
  children,
  className,
  padding = 'md',
  shadow = 'sm',
  border = true,
  hover = false,
  onClick
}) => {
  const cardClasses = clsx(
    'bg-white rounded-lg transition-all duration-200',
    
    // Padding classes
    padding === 'sm' && 'p-3',
    padding === 'md' && 'p-6',
    padding === 'lg' && 'p-8',
    
    // Shadow classes
    shadow === 'sm' && 'shadow-sm',
    shadow === 'md' && 'shadow',
    shadow === 'lg' && 'shadow-lg',
    
    // Border
    border && 'border border-gray-200',
    
    // Hover effects
    hover && 'hover:shadow-md hover:border-gray-300 cursor-pointer',
    
    className
  );

  return (
    <div className={cardClasses} onClick={onClick}>
      {children}
    </div>
  );
};

export interface CardHeaderProps {
  children: React.ReactNode;
  className?: string;
}

export const CardHeader: React.FC<CardHeaderProps> = ({ children, className }) => (
  <div className={clsx('border-b border-gray-200 pb-4 mb-6', className)}>
    {children}
  </div>
);

export interface CardTitleProps {
  children: React.ReactNode;
  className?: string;
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

export const CardTitle: React.FC<CardTitleProps> = ({ 
  children, 
  className,
  as: Component = 'h3'
}) => (
  <Component className={clsx('text-lg font-semibold text-gray-900', className)}>
    {children}
  </Component>
);

export interface CardContentProps {
  children: React.ReactNode;
  className?: string;
}

export const CardContent: React.FC<CardContentProps> = ({ children, className }) => (
  <div className={clsx('text-gray-600', className)}>
    {children}
  </div>
);

export interface CardFooterProps {
  children: React.ReactNode;
  className?: string;
}

export const CardFooter: React.FC<CardFooterProps> = ({ children, className }) => (
  <div className={clsx('border-t border-gray-200 pt-4 mt-6', className)}>
    {children}
  </div>
);